import React, { useCallback, useRef, useState } from 'react';
import { Card, CardContent, Typography, useTheme } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { fileToBase64 } from '../Document/DocumentOverview';
import {
  IDocumentWithScan,
  useUploadDocumentForInvoice,
} from '../hooks/reactQuerys/InvoiceQueries';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { useAlert } from '../context/AlertContext';
import { scanInvoicePdf } from './clientScan';

interface IProps {
  setInvoiceObjects: Function;
  setShowUploadSeciton: Function;
}

export const InvoiceCreateDialogUpload: React.FC<IProps> = ({
  setInvoiceObjects,
  setShowUploadSeciton,
}) => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const inputUpload = useRef<HTMLInputElement | null>(null);
  const { isPendingCheckDocumentForInvoice, uploadeDocumentForInvoice } =
    useUploadDocumentForInvoice();
  const [scanProgress, setScanProgress] = useState<string | null>(null);

  const handleFilesUpload = async (acceptedFiles: File[]) => {
    const uploadArray: IDocumentWithScan[] = [];
    let index = 0;

    for (const currentFile of acceptedFiles) {
      const fileName: string = String(currentFile['name']);
      if ('application/pdf' !== currentFile['type']) {
        index = index + 1;
        continue;
      }

      const base64Data = await fileToBase64(currentFile);
      const doc: IDocumentWithScan = {
        idDocument: -1 - index,
        FileName: fileName,
        idMIMEType: 1,
        Data: base64Data,
        Created_at: new Date().toISOString(),
        isFromTemplate: false,
      } as IDocumentWithScan;

      // Client-Scan: QR-Erkennung + Text-Layer-Analyse im Browser.
      // Schlaegt der QR-Scan fehl, versucht der Scanner ueber den
      // PDF-Text-Layer Urkundennummer/IBAN/Betrag zu lesen. Backend
      // faellt automatisch auf eigene Erkennung zurueck, wenn kein
      // ClientScan mitgesendet wird.
      setScanProgress(fileName);
      try {
        const buffer = await currentFile.arrayBuffer();
        const result = await scanInvoicePdf(buffer);
        const hasQr = !!result.qrPayload;
        const hasText = !!(
          result.combined.InvoiceNumber ||
          result.combined.Total ||
          result.combined.IBAN
        );
        if (hasQr || hasText) {
          doc.ClientScan = {
            EPCString: result.combined.EPCString,
            Total: result.combined.Total,
            InvoiceNumber: result.combined.InvoiceNumber,
            IBAN: result.combined.IBAN || undefined,
            BIC: result.combined.BIC || undefined,
            Recipient: result.combined.Recipient || undefined,
          };
        }
      } catch (e) {
        console.warn(
          '[Invoice] Client-Scan fehlgeschlagen, Backend uebernimmt:',
          e
        );
      }

      uploadArray.push(doc);
      index = index + 1;
    }

    setScanProgress(null);

    try {
      const uploaded = await uploadeDocumentForInvoice(uploadArray);
      setInvoiceObjects(uploaded);
      setShowUploadSeciton(false);
    } catch (error) {
      console.error(error);
      showAlert({
        severity: 'error',
        text: 'Die Erkennung der Dokumente war nicht erfolgreich!',
      });
    }
  };

  const handleNormalUpload = (files: null | FileList) => {
    if (files) {
      const acceptedFiles: File[] = Array.from(files);
      handleFilesUpload(acceptedFiles);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    handleFilesUpload(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  if (isPendingCheckDocumentForInvoice || scanProgress) {
    return (
      <>
        <LoadingSpinner height='200px' />
        {scanProgress && (
          <Typography variant='body2' color='text.secondary' align='center'>
            Lese „{scanProgress}" …
          </Typography>
        )}
      </>
    );
  }

  return (
    <>
      <input
        hidden
        ref={inputUpload}
        type='file'
        accept='.pdf'
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleNormalUpload(event.target.files)
        }
      />
      <Typography>
        Bitte laden Sie eine oder mehrere Rechnungen im PDF-Format hoch.
      </Typography>

      <Card
        variant='outlined'
        sx={{
          mt: 3,
          height: 150,
          cursor: 'pointer',
        }}
        onClick={() => inputUpload.current?.click()}
      >
        <CardContent sx={{ textAlign: 'center' }}>
          <div {...getRootProps()}>
            <input {...getInputProps()} accept='.pdf' />
            <UploadFile
              sx={{
                fontSize: 50,
                color: theme.palette.grey[400],
                mt: 4,
                ml: -2,
                animation: isDragActive ? 'blink 1s infinite' : 'none',
                '@keyframes blink': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
};
